import { useMemo, useState } from 'react';
import { Button, Card } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { ErrorState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { TierBadge } from '../../components/TierBadge';
import { VND } from '../../format';
import { tokens } from '../../theme';
import { tierApi, tierKeys } from '../tiers/api';
import {
  PAGE_SIZE,
  STATUS_LABELS,
  STATUS_TONE,
  displayName,
  formatPhoneNational,
  formatVnDate,
  memberKeys,
  membersApi,
  type MemberSearchRow,
} from './api';
import { SuperAdminGate } from './SuperAdminGate';

export function MembersPage() {
  return (
    <SuperAdminGate title="Thành viên">
      <MembersPageBody />
    </SuperAdminGate>
  );
}

function MembersPageBody() {
  const navigate = useNavigate();
  const hint = useDismissibleHint('members-search');

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const skip = (page - 1) * PAGE_SIZE;

  const listQuery = useQuery({
    queryKey: memberKeys.search(query, skip, PAGE_SIZE),
    queryFn: () => membersApi.search(query, skip, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const tiersQuery = useQuery({
    queryKey: tierKeys.all,
    queryFn: tierApi.list,
  });

  const tierName = useMemo(() => {
    const current = (tiersQuery.data ?? []).find((v) => v.isCurrentlyEffective);
    const map = new Map((current?.levels ?? []).map((l) => [l.code, l.name]));
    return (code: string | null) => (code ? (map.get(code) ?? code) : 'Chưa có hạng');
  }, [tiersQuery.data]);

  const columns: ColumnsType<MemberSearchRow> = [
    {
      title: 'Mã',
      dataIndex: 'memberCode',
      width: 120,
      render: (code: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{code}</span>
      ),
    },
    {
      title: 'Khách hàng',
      key: 'name',
      width: 220,
      ellipsis: true,
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>{displayName(row.name)}</div>
          <div style={{ fontSize: 12, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {formatPhoneNational(row.phone)}
          </div>
        </>
      ),
    },
    {
      title: 'Hạng thẻ',
      key: 'tier',
      width: 140,
      render: (_, row) => <TierBadge code={row.tierCode} name={tierName(row.tierCode)} />,
    },
    {
      title: 'Điểm dùng được',
      dataIndex: 'spendablePoints',
      width: 130,
      align: 'right',
      render: (points: number) => VND.format(points),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 150,
      render: (_, row) => (
        <StatusTag color={STATUS_TONE[row.status]}>{STATUS_LABELS[row.status]}</StatusTag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      width: 110,
      render: (iso: string) => formatVnDate(iso),
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      align: 'right',
      render: (_, row) => (
        <Button size="small" type="link" onClick={() => navigate(`/members/${row.id}`)}>
          Xem
        </Button>
      ),
    },
  ];

  const pageData = listQuery.data;

  return (
    <>
      <PageHeader title="Thành viên" subtitle="CRM" hint={<HintToggle hint={hint} />} />
      <PageBody>
        <HintBanner
          hint={hint}
          message="Tra cứu hội sở"
          description="Nhập mã thành viên, SĐT (từ 8 chữ số) hoặc tên. Đoạn SĐT ngắn hơn 8 số không được tra theo điện thoại — trùng quá nhiều người. Tra theo tên không có chỉ mục, ưu tiên mã hoặc SĐT."
        />

        {listQuery.isError ? (
          <ErrorState error={listQuery.error} onRetry={() => listQuery.refetch()} />
        ) : (
          <Card styles={{ body: { padding: tokens.space.cardPadding } }}>
            <DataTable<MemberSearchRow>
              rowKey="id"
              columns={columns}
              dataSource={pageData?.items ?? []}
              loading={listQuery.isFetching}
              tableLayout="fixed"
              scroll={{ x: 'max-content' }}
              onReload={() => listQuery.refetch()}
              title={pageData ? `${VND.format(pageData.total)} thành viên` : 'Thành viên'}
              subTitle={query ? `Kết quả cho “${query}”` : 'Mới đăng ký trước'}
              // Ô tìm built-in chạy SERVER-SIDE + debounce: gõ xong ngừng tay là tự tra, phân trang
              // qua API. Không cần nút "Tìm".
              searchPlaceholder="Tên, SĐT hoặc MV-99210"
              onSearch={(kw) => {
                setQuery(kw);
                setPage(1);
              }}
              locale={{
                emptyText: query
                  ? 'Không tìm thấy thành viên khớp. Thử mã, SĐT đủ 8 số, hoặc một phần tên. (SĐT dưới 8 chữ số không chạy đường tra điện thoại.)'
                  : 'Chưa có thành viên nào.',
              }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: pageData?.total ?? 0,
                showSizeChanger: false,
                onChange: (next) => setPage(next),
              }}
            />
          </Card>
        )}
      </PageBody>
    </>
  );
}
